import { useEffect, useState } from 'react';
import { Link } from 'wouter';
import { usePageMeta } from '../../lib/usePageMeta';
import { REPORT_PRICE } from '../../lib/pricing';
import { routes } from '../../lib/assessment/routes';
import { buttonVariants } from '../ui/button';
import { cn } from '../../lib/utils';
import { ArrowRight, CheckCircle, ChevronDown, Zap } from './icons';

/**
 * The Work Fit marketing page, migrated from the legacy application's
 * `src/pages/work-fit.tsx` (V2-018C-2).
 *
 * Parity migration. Copy, headings, section order, the ten dimensions, the
 * seven profiles, both inclusion lists, the interview-question and culture-risk
 * cards, the FAQ, the price reference, and the structured data are reproduced
 * exactly. The same four categories of change apply as on the previous pages,
 * and only those four: V2 routing, the Experience Shell owning the header and
 * footer, obsolete-technology removal (Clerk), and the accessibility flattening
 * of controls nested inside anchors.
 *
 * The footer disclaimer is the assessment-specific wording, which the shell
 * already supplies for this route through `disclaimerForPath`. Nothing on this
 * page sets it.
 *
 * As on the other assessment page, the FAQ accordion keeps its native
 * `<button>` and its open/closed state, and gains `aria-expanded` and
 * `aria-controls`, which the legacy markup omits.
 */
const DIMENSIONS = [
  {
    name: 'Autonomy Fit',
    desc: 'How much you need to own your approach and method without close oversight.',
  },
  {
    name: 'Structure Fit',
    desc: 'How much you rely on clear processes, defined roles, and predictable systems.',
  },
  {
    name: 'Pace Fit',
    desc: "How well the organization's urgency level matches your natural operating rhythm.",
  },
  {
    name: 'Decision-Making Fit',
    desc: 'How much you need decision authority that matches your accountability.',
  },
  {
    name: 'Feedback and Manager Style Fit',
    desc: 'What kind of management, feedback frequency, and direction you need to perform well.',
  },
  {
    name: 'Culture and Climate Fit',
    desc: 'The interpersonal and organizational environment that supports your work.',
  },
  {
    name: 'Energy and Sustainability Fit',
    desc: 'The pace and recovery culture that keeps your performance consistent over time.',
  },
  {
    name: 'Values and Trust Fit',
    desc: "Alignment between your integrity standards and the organization's actual behavior.",
  },
  {
    name: 'Conflict and Politics Tolerance',
    desc: 'How much organizational friction and internal dynamics drain your performance.',
  },
  {
    name: 'Role Clarity and Authority Fit',
    desc: 'How clearly your responsibilities, authority, and success criteria are defined.',
  },
];

const PROFILES = [
  {
    name: 'The Autonomous Builder',
    desc: 'Performs at the highest level when given genuine control over approach, method, and priorities. Constrained environments limit output regardless of talent.',
  },
  {
    name: 'The Strategic Operator',
    desc: 'Built for ownership of problems, decisions, and outcomes. Performs best when authority matches accountability and mandates are real.',
  },
  {
    name: 'The Systems-Focused High Performer',
    desc: 'Grounded in clarity. Works best when roles are well-defined, processes are documented, and expectations are explicit.',
  },
  {
    name: 'The Collaborative High Performer',
    desc: 'Culture is a performance variable. Works best in environments with genuine care, honest communication, and real investment in the people doing the work.',
  },
  {
    name: 'The Low-Politics Executor',
    desc: 'Performs best when outcomes matter more than optics. Political environments compete directly with execution bandwidth.',
  },
  {
    name: 'The Impact-Driven High Performer',
    desc: 'Most effective in environments that are sustainable, aligned with real values, and built on trust. Integrity mismatches erode performance faster than most expect.',
  },
  {
    name: 'The Growth-Edge High Performer',
    desc: 'Performs best at the edge of current ability. Needs environments that actively invest in development, not just deploy people in static roles.',
  },
];

const FREE_RESULT_INCLUDES = [
  'Your Work Fit Profile and overall fit result',
  'Core fit interpretation for your profile',
  'Your top 2 strongest environment needs',
  'Culture risk signals specific to your profile',
  'One targeted interview question for evaluating any role or organization',
];

const PAID_REPORT_INCLUDES = [
  'Complete dimension breakdown across all 10 dimensions',
  'Strongest fit areas',
  'Strongest misfit areas',
  'Environment pattern and what it means for your output',
  'Role, manager, and culture signals to look for',
  'Action plan for your current role and next opportunity',
  'Downloadable PDF',
];

const FAQ_ITEMS = [
  {
    q: 'What is the Work Fit Assessment?',
    a: 'It identifies the environment, manager style, culture pattern, and structural conditions where your performance style is most likely to deliver consistent output.',
  },
  {
    q: 'Who is the Work Fit Assessment for?',
    a: 'It is for high performers who are questioning whether their current role, manager, or culture is limiting their performance, or who want a clear framework for evaluating a new opportunity before accepting.',
  },
  {
    q: 'What does the assessment measure?',
    a: 'It measures 10 environment dimensions: Autonomy Fit, Structure Fit, Pace Fit, Decision-Making Fit, Feedback and Manager Style Fit, Culture and Climate Fit, Energy and Sustainability Fit, Values and Trust Fit, Conflict and Politics Tolerance, and Role Clarity and Authority Fit.',
  },
  {
    q: 'What do I get for free?',
    a: 'Your free result includes your Work Fit Profile, overall fit result, core fit interpretation, your top 2 strongest environment needs, culture risk signals specific to your profile, and one targeted interview question.',
  },
  {
    q: 'What is in the full report?',
    a: 'The full report includes your complete dimension breakdown across all 10 dimensions, strongest fit and misfit areas, your environment pattern, role and manager signals to look for, an action plan for your current role and next opportunity, and a downloadable PDF.',
  },
  {
    q: 'How is Work Fit different from Bottleneck and Perspective?',
    a: 'Bottleneck identifies the internal pattern creating performance drag. Work Fit identifies the environment conditions that fit how your performance style actually operates. Perspective identifies how your mind is interpreting what is happening.',
  },
];

export function WorkFit() {
  usePageMeta({
    title: 'High Performer Work Fit Assessment | High Performer OS',
    description:
      'The High Performer Work Fit Assessment helps you identify the work environment, manager style, culture, and support structure that fit how your performance style actually operates.',
    canonical: 'https://highperformeros.com/work-fit',
    ogImage: 'https://highperformeros.com/logo-white-v2.webp',
  });

  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'faq-schema-work-fit';
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQ_ITEMS.map(({ q, a }) => ({
        '@type': 'Question',
        name: q,
        acceptedAnswer: { '@type': 'Answer', text: a },
      })),
    });
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById('faq-schema-work-fit');
      if (el) document.head.removeChild(el);
    };
  }, []);

  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <>
      {/* Hero */}
      <section className="flex flex-col items-center justify-center px-4 py-24 text-center max-w-4xl mx-auto">
        <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-primary bg-primary/10 px-4 py-2 rounded-full mb-8">
          High Performer Work Fit Assessment · Free
        </div>
        <h1 className="text-4xl md:text-6xl font-bold tracking-tight mb-6 leading-tight">
          You might not be burned out.{' '}
          <span className="text-primary">You might be in the wrong work environment.</span>
        </h1>
        <p className="text-lg md:text-xl text-muted-foreground mb-10 max-w-2xl leading-relaxed">
          When the environment does not fit how you operate, the performance gap feels personal. It
          usually is not. The Work Fit Assessment helps you see what your role, culture, autonomy,
          pace, and manager style are contributing to that gap.
        </p>
        <Link
          href={routes.assessment('work_fit')}
          className={cn(
            buttonVariants({ size: 'lg' }),
            'text-base px-8 py-6 h-auto bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_30px_rgba(245,184,0,0.25)] gap-2',
            'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
          )}
        >
          Take the Work Fit Assessment
          <ArrowRight className="w-4 h-4" />
        </Link>
        <p className="text-xs text-muted-foreground mt-4">
          50 questions. Free. No credit card required.
        </p>
      </section>

      {/* AISEO Answer Block */}
      <section className="py-16 px-4 border-y border-border bg-card">
        <div className="max-w-3xl mx-auto space-y-8">
          <div className="space-y-1">
            <h2 className="text-sm font-bold uppercase tracking-widest text-primary">
              What is the Work Fit Assessment?
            </h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              The Work Fit Assessment is a 50-question diagnostic that identifies the environment,
              manager style, culture pattern, and structural conditions where your performance style
              is most likely to deliver consistent output. It reveals where your operating
              requirements are being met and where they are being suppressed. The result is free.
            </p>
          </div>
          <div className="space-y-1">
            <h2 className="text-sm font-bold uppercase tracking-widest text-primary">
              Who is it for?
            </h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              It is for high performers who are questioning whether their current role, manager, or
              culture is limiting their performance, or who want a clear framework for evaluating a
              new opportunity before accepting.
            </p>
          </div>
          <div className="space-y-1">
            <h2 className="text-sm font-bold uppercase tracking-widest text-primary">
              What does it measure?
            </h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              It measures 10 environment dimensions: Autonomy Fit, Structure Fit, Pace Fit,
              Decision-Making Fit, Feedback and Manager Style Fit, Culture and Climate Fit, Energy
              and Sustainability Fit, Values and Trust Fit, Conflict and Politics Tolerance, and Role
              Clarity and Authority Fit.
            </p>
          </div>
          <div className="space-y-1">
            <h2 className="text-sm font-bold uppercase tracking-widest text-primary">
              What do you get for free?
            </h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Your free result includes your Work Fit Profile, overall fit result, core fit
              interpretation, your top 2 strongest environment needs, culture risk signals specific
              to your profile, and one targeted interview question. No payment required.
            </p>
          </div>
          <div className="space-y-1">
            <h2 className="text-sm font-bold uppercase tracking-widest text-primary">
              What is included in the full report?
            </h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              The full report includes your complete dimension breakdown across all 10 dimensions,
              strongest fit and misfit areas, your environment pattern, role and manager signals to
              look for, an action plan for your current role and next opportunity, and a
              downloadable PDF. It is a one-time purchase unlocked after your free result.
            </p>
          </div>
        </div>
      </section>

      {/* What it helps you see */}
      <section className="bg-card border-y border-border py-20 px-4">
        <div className="max-w-3xl mx-auto text-center space-y-6">
          <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
            What the Work Fit Assessment helps you see
          </p>
          <h2 className="text-2xl md:text-3xl font-bold">
            High performers often blame themselves for an environment problem.
          </h2>
          <div className="space-y-4 text-muted-foreground leading-relaxed text-left">
            <p>
              When output drops or engagement fades, the default assumption is that something is
              wrong with you. You are not focused enough, not resilient enough, not committed
              enough. But sometimes the issue is not internal. Sometimes the role, the culture, the
              pace, the autonomy, or the decision space does not fit how you operate best.
            </p>
            <p>
              The Work Fit Assessment identifies your performance style requirements across 10
              dimensions. It shows you the environments, manager styles, culture patterns, and
              structural conditions where your performance actually delivers. It also shows you the
              conditions most likely to quietly suppress it.
            </p>
            <p>
              The result is not a personality type. It is a practical map you can use to evaluate
              your current role or any future opportunity before you commit.
            </p>
          </div>
        </div>
      </section>

      {/* What it measures */}
      <section className="py-20 px-4">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-3">What it measures</h2>
          <p className="text-center text-muted-foreground mb-12 max-w-xl mx-auto">
            50 questions across 10 dimensions. Each dimension maps a specific environmental factor
            to your performance style.
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            {DIMENSIONS.map((d) => (
              <div key={d.name} className="p-5 rounded-xl border border-border bg-card space-y-2">
                <h4 className="font-bold text-sm">{d.name}</h4>
                <p className="text-xs text-muted-foreground leading-relaxed">{d.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Free result or full report? */}
      <section className="bg-card border-y border-border py-20 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3">
              Compare
            </p>
            <h2 className="text-2xl md:text-3xl font-bold mb-3">Free result or full report?</h2>
            <p className="text-center text-muted-foreground max-w-xl mx-auto">
              Start with the free result. Unlock the full report when you want the complete
              dimension breakdown and a concrete plan for evaluating your current role or next
              opportunity.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="rounded-2xl border border-border bg-background p-7 space-y-5">
              <div>
                <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                  Free
                </span>
                <h3 className="text-xl font-bold mt-1">Free Result</h3>
              </div>
              <ul className="space-y-3">
                {FREE_RESULT_INCLUDES.map((item) => (
                  <li key={item} className="flex gap-3 items-start text-sm">
                    <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link
                href={routes.assessment('work_fit')}
                className={cn(
                  buttonVariants({ variant: 'outline' }),
                  'w-full mt-2',
                  'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
                )}
              >
                Take the free assessment
              </Link>
            </div>

            <div className="rounded-2xl border-2 border-primary/40 bg-primary/5 p-7 space-y-5 relative">
              <div className="absolute -top-3 left-6">
                <span className="text-xs font-bold uppercase tracking-widest bg-primary text-primary-foreground px-3 py-1 rounded-full">
                  {`Full Report · $${REPORT_PRICE}`}
                </span>
              </div>
              <div className="pt-2">
                <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                  One-time payment
                </span>
                <h3 className="text-xl font-bold mt-1">Full Report</h3>
              </div>
              <ul className="space-y-3">
                {PAID_REPORT_INCLUDES.map((item) => (
                  <li key={item} className="flex gap-3 items-start text-sm">
                    <CheckCircle className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                    {item}
                  </li>
                ))}
              </ul>
              <p className="text-xs text-muted-foreground">Unlock after your result.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Work Fit Profiles */}
      <section className="py-20 px-4">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-3">
            The 7 Work Fit Profiles
          </h2>
          <p className="text-center text-muted-foreground mb-12 max-w-xl mx-auto">
            Your profile is determined by your highest-scoring environment dimensions. Each profile
            describes a distinct set of performance requirements.
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            {PROFILES.map((p) => (
              <div key={p.name} className="rounded-xl border border-border bg-card p-5 space-y-2">
                <Zap className="w-4 h-4 text-primary" />
                <h4 className="font-bold text-sm">{p.name}</h4>
                <p className="text-xs text-muted-foreground leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Interview Questions and Red Flags */}
      <section className="bg-card border-y border-border py-20 px-4">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-3">
            Interview questions and red flags
          </h2>
          <p className="text-center text-muted-foreground mb-10 max-w-xl mx-auto">
            Your results include a targeted interview question specific to your profile. You can use
            it to evaluate any role or organization before you accept.
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="p-6 rounded-xl border border-border bg-background space-y-3">
              <h3 className="font-bold">Interview question</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Each Work Fit Profile comes with one high-signal interview question tailored to the
                environment factor most critical for your performance. The question is designed to
                surface how a role, team, or organization actually operates, not just how it
                presents in an interview.
              </p>
            </div>
            <div className="p-6 rounded-xl border border-border bg-background space-y-3">
              <h3 className="font-bold">Culture risk signals</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Your results include two culture risk signals specific to your profile. These are
                the organizational patterns most likely to suppress your performance style, even in
                environments that look strong on paper. Knowing them in advance changes how you
                evaluate opportunities.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 px-4">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-10">
            Frequently asked questions
          </h2>
          <div className="space-y-3" data-testid="faq-list">
            {FAQ_ITEMS.map((item, i) => (
              <div key={i} className="border border-border rounded-xl overflow-hidden">
                <button
                  type="button"
                  aria-expanded={openFaq === i}
                  aria-controls={`faq-panel-${i}`}
                  data-testid={`faq-trigger-${i}`}
                  className="w-full flex items-center justify-between px-5 py-4 text-left text-sm font-semibold hover:bg-card transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                >
                  {item.q}
                  <ChevronDown
                    className={`w-4 h-4 shrink-0 ml-4 text-muted-foreground transition-transform duration-150 ${openFaq === i ? 'rotate-180' : ''}`}
                  />
                </button>
                {openFaq === i && (
                  <div
                    id={`faq-panel-${i}`}
                    className="px-5 pb-4 text-sm text-muted-foreground leading-relaxed border-t border-border pt-3"
                  >
                    {item.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-4 text-center">
        <div className="max-w-2xl mx-auto space-y-6">
          <h2 className="text-2xl md:text-3xl font-bold">
            Find out if the environment is fighting how you perform.
          </h2>
          <p className="text-muted-foreground">
            50 questions. Free results. See how your role, culture, autonomy, pace, and manager
            style align with how you actually perform best.
          </p>
          <Link
            href={routes.assessment('work_fit')}
            className={cn(
              buttonVariants({ size: 'lg' }),
              'text-base px-8 py-6 h-auto bg-primary text-primary-foreground hover:bg-primary/90 gap-2',
              'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
            )}
          >
            Take the Work Fit Assessment
            <ArrowRight className="w-4 h-4" />
          </Link>
          <p className="text-sm text-muted-foreground">
            Looking for Bottleneck instead?{' '}
            <Link
              href={routes.bottleneck}
              className="underline hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            >
              Explore the Bottleneck Assessment.
            </Link>
          </p>
          <p className="text-sm text-muted-foreground">
            Looking for Perspective instead?{' '}
            <Link
              href={routes.perspective}
              className="underline hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            >
              Explore the Perspective Assessment.
            </Link>
          </p>
        </div>
      </section>
    </>
  );
}

export { FAQ_ITEMS as WORK_FIT_FAQ_ITEMS, DIMENSIONS, PROFILES };
